import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  Code2,
  Globe2,
  Landmark,
  LayoutDashboard,
  Link2,
  Mail,
  QrCode,
  ReceiptText,
  Rows3,
  ShieldCheck,
  ShoppingBag,
  Store,
  Users,
  WalletCards,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SectionShell from "@/components/SectionShell";
import {
  individualAudiences,
  individualBenefits,
  merchantBenefits,
  merchantRoadmap,
  partnerCategories,
  trustPrinciples,
  whyKoinecktaCards,
} from "@/data/siteContent";

const iconMap = {
  users: Users,
  landmark: Landmark,
  store: Store,
  receipt: ReceiptText,
  link: Link2,
  shopping: ShoppingBag,
  qr: QrCode,
  layout: LayoutDashboard,
  code: Code2,
  rows: Rows3,
};

const JourneyLane = ({ label, steps, accent = "teal", testId }) => (
  <div className={`rounded-[28px] border p-6 ${accent === "lime" ? "border-[#6AE32F]/30 bg-[#6AE32F]/8" : "border-[#3ABDBC]/25 bg-[#08133E]/88"}`} data-testid={testId}>
    <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accent === "lime" ? "text-[#6AE32F]" : "text-[#45D6D1]"}`}>{label}</p>
    <div className="mt-5 flex flex-wrap items-center gap-3">
      {steps.map((step, index) => (
        <div key={step} className="contents">
          <span className="rounded-2xl border border-[#303B67] bg-[#010B30] px-4 py-3 text-sm font-semibold text-white">{step}</span>
          {index < steps.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-[#6AE32F]" />}
        </div>
      ))}
    </div>
  </div>
);

const FeatureGrid = ({ items, testId, columns = "lg:grid-cols-4" }) => (
  <div className={`grid gap-5 sm:grid-cols-2 ${columns}`} data-testid={testId}>
    {items.map((item) => {
      const Icon = iconMap[item.icon] || CheckCircle2;
      return (
        <Card key={item.title} className="border-[#3ABDBC]/20 bg-[#08133E]/88" data-testid={`${testId}-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <CardContent className="p-6">
            <div className="mb-5 inline-flex rounded-2xl bg-[#3ABDBC]/12 p-3 text-[#45D6D1]"><Icon className="h-5 w-5" /></div>
            <h3 className="font-display text-xl font-semibold text-white">{item.title}</h3>
            <p className="mt-3 text-sm leading-7 text-[#B8C4DC]">{item.body}</p>
          </CardContent>
        </Card>
      );
    })}
  </div>
);

export const DifferenceSection = () => (
  <SectionShell id="difference" eyebrow="How Koineckta is different" title="More than a crypto off-ramp" description="Traditional crypto off-ramp journeys are built around converting digital assets into cash before the user can spend them. Koineckta is being designed around the payment itself.">
    <div className="grid gap-5 lg:grid-cols-2">
      <JourneyLane label="Traditional cash-out journey" steps={["Digital Asset", "Convert", "Withdraw", "Bank", "Pay"]} testId="traditional-payment-journey" />
      <JourneyLane label="Koineckta payment journey" steps={["Digital Asset", "Pay", "Merchant receives Naira"]} accent="lime" testId="koineckta-payment-journey" />
    </div>
    <p className="border-l-2 border-[#6AE32F] pl-5 font-display text-2xl font-semibold text-white" data-testid="difference-closing-statement">Koineckta is being built around payments, not trading.</p>
  </SectionShell>
);

export const MerchantSection = ({ onJoinPilot }) => (
  <SectionShell id="merchants" eyebrow="For Merchants" title="Accept digital assets. Get paid in Naira." description="Give customers another way to pay without changing how your business prefers to settle. Koineckta is designed to handle the digital-asset complexity while merchants continue operating in familiar local currency.">
    <FeatureGrid items={merchantBenefits} testId="merchant-benefit-grid" />
    <Button onClick={onJoinPilot} className="h-12 w-fit rounded-full bg-[#6AE32F] px-7 text-[#010B30] hover:bg-[#82EF4D]" data-testid="merchant-primary-cta">Join the Merchant Pilot</Button>
  </SectionShell>
);

export const TreasurySection = () => (
  <section className="px-5 py-8 sm:px-8 lg:px-10" data-testid="no-crypto-treasury-section">
    <div className="mx-auto grid max-w-7xl gap-8 overflow-hidden rounded-[32px] border border-[#3ABDBC]/25 bg-gradient-to-br from-[#081C4B] via-[#07566A] to-[#087E7B] p-8 sm:p-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
      <div className="inline-flex h-20 w-20 items-center justify-center rounded-3xl border border-white/15 bg-[#010B30]/35 text-[#6AE32F]"><BriefcaseBusiness className="h-9 w-9" /></div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#B7F5F1]">No crypto treasury required</p>
        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight text-white sm:text-4xl" data-testid="treasury-section-title">Accept digital-asset payments without running your business in crypto.</h2>
        <p className="mt-5 text-base leading-8 text-[#E2F5F4]">Your customer can pay using supported digital assets while your business receives local currency. Koineckta is designed to reduce the need for manual conversion workflows, trading processes, wallet management and digital-asset treasury complexity on the merchant side.</p>
      </div>
    </div>
  </section>
);

export const DirectSpendSection = () => (
  <SectionShell id="directspend" eyebrow="Product vision • Planned experience" title="Spend digital assets without manually cashing out first" description="Koineckta DirectSpend is the payment experience being designed to reduce the traditional sequence of selling, withdrawing and then paying. The goal is a simpler journey where digital value can fund an everyday payment directly.">
    <div className="rounded-[32px] border border-[#3ABDBC]/22 bg-[#08133E]/90 p-6 sm:p-8">
      <div className="mb-7 flex items-center gap-3"><WalletCards className="h-7 w-7 text-[#6AE32F]" /><h3 className="font-display text-2xl font-semibold text-white" data-testid="directspend-product-name">Koineckta DirectSpend</h3></div>
      <div className="grid gap-5 lg:grid-cols-2">
        <JourneyLane label="Traditional" steps={["USDT", "Exchange", "Sell", "Naira", "Withdraw", "Pay Merchant"]} testId="directspend-traditional-journey" />
        <JourneyLane label="Koineckta DirectSpend" steps={["USDT", "Pay Merchant", "Merchant receives Naira"]} accent="lime" testId="directspend-koineckta-journey" />
      </div>
      <p className="mt-6 text-sm leading-7 text-[#9BA8C6]" data-testid="directspend-caption">Planned pilot experience. Availability will depend on supported assets, wallets and settlement partners.</p>
    </div>
  </SectionShell>
);

export const MerchantRoadmapSection = () => (
  <SectionShell id="merchant-roadmap" eyebrow="Merchant acceptance roadmap" title="Built around modern merchant acceptance" description="Koineckta’s product roadmap is centred on making digital-asset-funded payments feel as familiar as modern fintech checkout.">
    <FeatureGrid items={merchantRoadmap} testId="merchant-roadmap-grid" columns="lg:grid-cols-3" />
    <p className="text-sm leading-7 text-[#9BA8C6]" data-testid="merchant-roadmap-disclosure">Pilot functionality will be introduced progressively based on technical, partner and regulatory readiness.</p>
  </SectionShell>
);

export const IndividualSection = ({ onJoinEarlyAccess }) => (
  <SectionShell id="individuals" eyebrow="For Individuals" title="Use your digital assets — not just hold or trade them" description="Koineckta is being designed for people who earn, hold or receive digital value and want a simpler path into everyday payments.">
    <div className="flex flex-wrap gap-2" data-testid="individual-audience-list">{individualAudiences.map((audience) => <span key={audience} className="rounded-full border border-[#3ABDBC]/22 bg-[#08133E] px-4 py-2 text-sm text-[#DCE4F4]">{audience}</span>)}</div>
    <FeatureGrid items={individualBenefits} testId="individual-benefit-grid" />
    <Button onClick={onJoinEarlyAccess} variant="outline" className="h-12 w-fit rounded-full border-[#3ABDBC]/40 bg-[#08133E] px-7 text-white hover:bg-[#121E4E]" data-testid="individual-primary-cta">Join Early Access</Button>
  </SectionShell>
);

export const PaymentScenarioSection = () => {
  const steps = ["Customer selects an item priced at ₦50,000", "Merchant presents a Koineckta payment request", "Customer selects a supported digital asset", "Customer completes the payment", "Merchant receives ₦50,000"];
  return (
    <SectionShell id="payment-scenario" eyebrow="Illustrative example" title="What a Koineckta payment could look like" description="A simple example of how digital value and local-currency commerce can meet in one payment journey.">
      <div className="grid gap-3 lg:grid-cols-5" data-testid="payment-scenario-steps">{steps.map((step, index) => <div key={step} className={`rounded-[24px] border p-5 ${index === 4 ? "border-[#6AE32F]/35 bg-[#6AE32F]/10" : "border-[#3ABDBC]/20 bg-[#08133E]/88"}`} data-testid={`payment-scenario-step-${index + 1}`}><span className="text-xs font-semibold text-[#45D6D1]">0{index + 1}</span><p className="mt-3 text-sm font-semibold leading-6 text-white">{step}</p></div>)}</div>
      <p className="font-display text-2xl font-semibold text-white" data-testid="payment-scenario-closing">Digital asset on one side. Local currency on the other. <span className="text-[#6AE32F]">Koineckta in the middle.</span></p>
      <p className="text-sm text-[#9BA8C6]">Illustrative pilot journey only. It is not a live or guaranteed transaction.</p>
    </SectionShell>
  );
};

export const WhyKoinecktaSection = () => (
  <SectionShell id="why-koineckta" eyebrow="Why Koineckta" title="Payment infrastructure designed for real commerce" description="Not another crypto exchange. A new way to pay — designed around merchant acceptance, local settlement and everyday utility.">
    <FeatureGrid items={whyKoinecktaCards} testId="why-koineckta-grid" columns="lg:grid-cols-5" />
  </SectionShell>
);

export const AfricaVisionSection = () => (
  <SectionShell id="africa-vision" eyebrow="Nigeria First" title="Starting with Naira. Built for African commerce." description="Koineckta’s initial focus is Nigeria. The longer-term vision is to connect digital value with local payment and settlement rails across African markets.">
    <div className="flex flex-col items-stretch gap-3 rounded-[32px] border border-[#3ABDBC]/22 bg-[#08133E]/88 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8" data-testid="africa-roadmap">
      {["Nigeria / NGN", "West African markets", "Wider African payment rails"].map((stage, index) => <div key={stage} className="contents"><div className={`rounded-2xl border px-5 py-4 text-center text-sm font-semibold ${index === 0 ? "border-[#6AE32F]/40 bg-[#6AE32F]/10 text-[#6AE32F]" : "border-[#303B67] bg-[#010B30] text-white"}`}>{stage}</div>{index < 2 && <ArrowRight className="mx-auto h-5 w-5 rotate-90 text-[#45D6D1] sm:rotate-0" />}</div>)}
    </div>
    <p className="font-display text-2xl font-semibold text-white" data-testid="africa-vision-line">One payment layer. Multiple local economies.</p>
  </SectionShell>
);

export const PartnershipSection = ({ partnershipHref }) => (
  <SectionShell id="partners" eyebrow="Partners" title="Building the payment layer together" description="Koineckta’s model depends on strong financial, technology and ecosystem partnerships. We welcome conversations with organisations interested in connecting digital assets to local commerce.">
    <div className="grid gap-6 lg:grid-cols-[1fr_0.42fr] lg:items-stretch">
      <div className="grid gap-3 sm:grid-cols-2" data-testid="partner-category-grid">{partnerCategories.map((category) => <div key={category} className="flex items-center gap-3 rounded-2xl border border-[#3ABDBC]/20 bg-[#08133E]/88 px-4 py-4 text-sm font-medium text-[#DCE4F4]"><Globe2 className="h-4 w-4 shrink-0 text-[#45D6D1]" />{category}</div>)}</div>
      <Card className="border-[#6AE32F]/25 bg-[#6AE32F]/10"><CardContent className="flex h-full flex-col justify-between gap-8 p-7"><div><Mail className="h-7 w-7 text-[#6AE32F]" /><h3 className="mt-5 font-display text-2xl font-semibold text-white">Start a partnership conversation</h3><p className="mt-3 text-sm leading-7 text-[#B8C4DC]">Tell us how your infrastructure, network or platform could support the Koineckta payment layer.</p></div><a href={partnershipHref} className="inline-flex h-12 items-center justify-center rounded-full bg-[#6AE32F] px-6 text-sm font-semibold text-[#010B30] hover:bg-[#82EF4D]" data-testid="partnership-email-cta">Explore a Partnership</a></CardContent></Card>
    </div>
  </SectionShell>
);

export const TrustSection = () => (
  <SectionShell id="trust" eyebrow="Responsible rollout" title="Trust is part of the product architecture" description="Koineckta is preparing for pilot launch with a conservative approach to security, compliance, operational readiness and communication.">
    <FeatureGrid items={trustPrinciples.map((item) => ({ ...item, icon: "shield" }))} testId="trust-principle-grid" />
    <div className="rounded-2xl border border-[#3ABDBC]/22 bg-[#010B30] p-5 text-sm leading-7 text-[#B8C4DC]" data-testid="site-stage-disclosure"><ShieldCheck className="mr-2 inline h-4 w-4 text-[#6AE32F]" />Koineckta is currently in development and does not yet provide live transactional services. Product availability, supported assets, settlement methods and rollout timelines remain subject to technical, commercial and regulatory readiness.</div>
  </SectionShell>
);